/**
 * TRACK 3 — Persistent Workspace.
 *
 * The platform remembers what the operator was last doing so the 5:30 AM
 * resume is instant: last project, last sheet, last zoom, last tool, last
 * filters. All on a single localStorage namespace so it's easy to reset
 * during testing.
 *
 * No backend dependency — workspace memory is per-device by design, the
 * same way Bluebeam handles "last document".
 */
const NS = "fop_ws_v1";

function _read() {
  try {
    const raw = localStorage.getItem(NS);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function _write(state) {
  try {
    localStorage.setItem(NS, JSON.stringify(state));
  } catch {
    /* quota or private mode — swallow */
  }
}

export const workspace = {
  // Last sheet the operator viewed (used by dashboard Resume card)
  setLastSheet({ sheet_id, project_id, sheet_number, sheet_title, opened_at }) {
    const s = _read();
    s.last_sheet = {
      sheet_id,
      project_id,
      sheet_number,
      sheet_title,
      opened_at: opened_at || new Date().toISOString(),
    };
    _write(s);
  },
  getLastSheet() {
    return _read().last_sheet || null;
  },

  // Last project the operator opened (project page)
  setLastProject({ project_id, project_name }) {
    const s = _read();
    s.last_project = {
      project_id,
      project_name,
      opened_at: new Date().toISOString(),
    };
    _write(s);
  },
  getLastProject() {
    return _read().last_project || null;
  },

  // Per-sheet viewport so zooming back to the same sheet feels instant.
  setSheetViewport(sheet_id, { scale, tx, ty }) {
    if (!sheet_id) return;
    const s = _read();
    s.viewports = s.viewports || {};
    s.viewports[sheet_id] = { scale, tx, ty, saved_at: Date.now() };
    // Keep only the 25 most recent so localStorage doesn't drift.
    const entries = Object.entries(s.viewports);
    if (entries.length > 25) {
      entries.sort((a, b) => (b[1].saved_at || 0) - (a[1].saved_at || 0));
      s.viewports = Object.fromEntries(entries.slice(0, 25));
    }
    _write(s);
  },
  getSheetViewport(sheet_id) {
    if (!sheet_id) return null;
    const s = _read();
    return (s.viewports && s.viewports[sheet_id]) || null;
  },

  // Last tool active in the viewer (so reopening the sheet preselects it).
  setLastTool(tool) {
    const s = _read();
    s.last_tool = tool || "pan";
    _write(s);
  },
  getLastTool() {
    return _read().last_tool || "pan";
  },

  // Project page filters (discipline picker + search).
  setProjectFilters(project_id, { discipline, q }) {
    if (!project_id) return;
    const s = _read();
    s.project_filters = s.project_filters || {};
    s.project_filters[project_id] = { discipline: discipline || "", q: q || "" };
    _write(s);
  },
  getProjectFilters(project_id) {
    if (!project_id) return { discipline: "", q: "" };
    const s = _read();
    return (s.project_filters && s.project_filters[project_id]) || { discipline: "", q: "" };
  },

  // Hard reset (test hook / Sign Out wiring)
  reset() {
    _write({});
  },
};

export default workspace;
